import { categoryIdsFrom } from './placeCategories';
import type { LocalPlace } from '../data/localDb';
import type { PlacePhone, PlaceReviews, PlaceSocialNetwork } from '../types/place';
import { validatePhone } from './validatePhone';

export interface NormalizePlaceContext {
  listId: string;
  createdAt?: string;
  /** Centro da busca [latitude, longitude]; mantém a regra atual de no máximo 50 km. */
  zeroPoint?: readonly number[];
  /** ISO-2 usado para interpretar telefones nacionais do place. */
  country?: string;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
}

function numberValue(value: unknown): number | undefined {
  if (value === '' || value == null) return undefined;
  const normalized = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(normalized) ? normalized : undefined;
}

function stringArray(value: unknown, lowercase = false): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result = new Set<string>();
  for (const item of value) {
    const normalized = text(item);
    if (normalized) result.add(lowercase ? normalized.toLowerCase() : normalized);
  }
  return result.size > 0 ? [...result] : undefined;
}

function unknownArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) && value.length > 0 ? value : undefined;
}

function location(value: unknown): number[] | undefined {
  if (!Array.isArray(value) || value.length < 2) return undefined;
  const latitude = numberValue(value[0]);
  const longitude = numberValue(value[1]);
  if (latitude == null || longitude == null) return undefined;
  return [latitude, longitude];
}

function socialNetworks(value: unknown): PlaceSocialNetwork[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result = new Map<string, PlaceSocialNetwork>();

  for (const item of value) {
    if (!isRecord(item)) continue;
    const url = text(item.url);
    const name = text(item.name);
    if (url && name && !result.has(url)) result.set(url, { url, name });
  }
  return result.size > 0 ? [...result.values()] : undefined;
}

function clampScore(value: number | undefined): number | undefined {
  return value == null ? undefined : Math.min(100, Math.max(0, Math.round(value)));
}

function positiveInteger(value: number | undefined): number | undefined {
  return value != null && Number.isInteger(value) && value >= 1 ? value : undefined;
}

/** CIDs decimais, sem repetir; o Google manda no máximo 20 parecidos por negócio. */
function cids(value: unknown, max = 40): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result = new Set<string>();
  for (const item of value) {
    const cid = text(item);
    if (cid && /^\d{1,24}$/.test(cid) && cid !== '0') result.add(cid);
    if (result.size >= max) break;
  }
  return result.size > 0 ? [...result] : undefined;
}

/**
 * País do endereço do Maps (`row[183][1]`), que fica na posição 6. Lido do array bruto: o
 * `stringArray` tira repetidos (as posições 1 e 2 vêm iguais) e o Google pode acrescentar
 * posições depois do país, como o andar (`["Andar: 2"]`, visto em 25/10/2026).
 */
function addressCountry(value: unknown): string | undefined {
  if (!Array.isArray(value) || value.length < 7) return undefined;
  return isoCountry(value[6]);
}

function isoCountry(value: unknown): string | undefined {
  const country = text(value);
  return country && /^[a-z]{2}$/i.test(country) ? country.toUpperCase() : undefined;
}

/**
 * Reclassifica os telefones de um place já gravado sem país (até 25/10/2026 o painel não lia o
 * país do endereço do Maps e o backend não o lia com o andar). O endereço local já passou pelo
 * `stringArray`, então o país é a posição 6 do bruto ou, depois do `Set`, a última. Telefones já
 * classificados ficam como estão. Devolve `undefined` quando não há o que mudar.
 */
export function repairPlacePhones(place: LocalPlace): LocalPlace | undefined {
  const unclassified = (phone: unknown): phone is PlacePhone =>
    isRecord(phone) && typeof phone.mobile !== 'boolean';
  if (!Array.isArray(place.phones) || !place.phones.some(unclassified)) return undefined;
  const address: unknown[] = Array.isArray(place.address) ? place.address : [];
  const country = addressCountry(address) ?? isoCountry(address[address.length - 1]);
  if (!country) return undefined;

  let changed = false;
  const repaired = new Map<string, PlacePhone>();
  for (const phone of place.phones) {
    let next = phone;
    if (unclassified(phone)) {
      const validated = validatePhone(phone.number ?? phone.formated, country);
      if (validated.status !== 'invalid' && validated.number?.startsWith('+')) {
        next = {
          number: validated.number,
          formated: validated.formatted ?? phone.formated,
          mobile: validated.mobile,
        };
        changed = true;
      }
    }
    // Dois formatos do mesmo número viram um só; o que não é telefone passa como veio.
    const key =
      (isRecord(next) && (text(next.number) ?? text(next.formated))) || `#${repaired.size}`;
    if (!repaired.has(key)) repaired.set(key, next);
  }
  if (!changed) return undefined;

  const phonesAfter = [...repaired.values()];
  const anyMobile = phonesAfter.some((phone) => isRecord(phone) && phone.mobile === true);
  return {
    ...place,
    phones: phonesAfter,
    mobile: place.mobile === true || anyMobile || undefined,
  };
}

function phones(value: unknown, country?: string): PlacePhone[] | undefined {
  const candidates = Array.isArray(value) ? value : value == null ? [] : [value];
  const result = new Map<string, PlacePhone>();

  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    const rawNumber =
      text(candidate.number) ?? text(candidate.formated) ?? text(candidate.formatted);
    const validated = validatePhone(rawNumber, country);
    if (validated.status === 'invalid' || !validated.number) continue;
    const number = validated.number;
    const formated = validated.formatted ?? text(candidate.formated) ?? text(candidate.formatted);
    const key = number;
    if (!key || result.has(key)) continue;
    result.set(key, {
      number,
      formated,
      mobile:
        validated.mobile ?? (typeof candidate.mobile === 'boolean' ? candidate.mobile : undefined),
    });
  }
  return result.size > 0 ? [...result.values()] : undefined;
}

function reviews(value: unknown): PlaceReviews | undefined {
  if (!isRecord(value)) return undefined;
  const count = numberValue(value.count);
  const avg = numberValue(value.avg);
  const rating = Array.isArray(value.rating)
    ? value.rating.map(numberValue).filter((item): item is number => item != null)
    : undefined;
  if (count == null && avg == null && !rating?.length) return undefined;
  return { count, avg, rating: rating?.length ? rating : undefined };
}

function enhancedCount(value: unknown): LocalPlace['enhancedCount'] {
  if (!isRecord(value)) return undefined;
  const result: NonNullable<LocalPlace['enhancedCount']> = {
    websites: numberValue(value.websites),
    emails: numberValue(value.emails),
    social_networks: numberValue(value.social_networks),
    phones: numberValue(value.phones),
  };
  return Object.values(result).some((item) => item != null) ? result : undefined;
}

/** Haversine em metros, usado para manter o recorte de 50 km do fluxo atual. */
export function distanceBetweenPlaces(
  a: readonly number[],
  b: readonly number[],
): number | undefined {
  if (a.length < 2 || b.length < 2 || !a.every(Number.isFinite) || !b.every(Number.isFinite)) {
    return undefined;
  }
  const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
  const latitudeA = toRadians(a[0]!);
  const latitudeB = toRadians(b[0]!);
  const latitudeDelta = toRadians(b[0]! - a[0]!);
  const longitudeDelta = toRadians(b[1]! - a[1]!);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)) * 6_371_000;
}

/** Porte de `getScorePlace` do backend; mantém a ordenação usada pelo LeadMaps. */
export function scorePlace(place: Partial<LocalPlace>): number {
  let score = 0;
  if (place.ads) score += 10;
  if (!place.claim) score += 10;
  if (place.image) score += 10;

  const photos = place.photos ?? 0;
  if (photos > 10) score += 10;
  else if (photos > 0 && photos < 10) score += photos;
  else if (place.image) score += 1;

  if ((place.times?.length ?? 0) > 2) score += 10;
  if ((place.websites?.length ?? 0) > 0) score += 10;
  if (place.phones?.some((phone) => phone.number)) score += 10;

  const posts = place.posts ?? 0;
  if (posts > 10) score += 10;
  else if (posts > 0 && posts < 10) score += posts;

  const reviewCount = place.reviews?.count ?? 0;
  if (reviewCount > 0) score += Math.min(10, Math.max(0, Math.round(reviewCount / 10)));
  const reviewAverage = place.reviews?.avg ?? 0;
  if (reviewAverage > 0) {
    const multiplier = reviewCount > 250 ? 4 : reviewCount > 50 ? 2 : 1;
    score += Math.round(reviewAverage * multiplier);
  }
  return Math.min(100, score);
}

function normalizePlace(
  raw: UnknownRecord,
  context: Required<Pick<NormalizePlaceContext, 'listId' | 'createdAt'>> & { country?: string },
): LocalPlace | undefined {
  const cid = text(raw.cid);
  if (!cid) return undefined;

  const address = stringArray(raw.address);
  const normalizedPhones = phones(raw.phones, addressCountry(raw.address) ?? context.country);
  const normalized: LocalPlace = {
    listId: context.listId,
    key: `cid:${cid}`,
    cid,
    placeID: text(raw.placeID),
    title: text(raw.title),
    location: location(raw.location),
    image: text(raw.image),
    images: stringArray(raw.images),
    photos: numberValue(raw.photos),
    posts: numberValue(raw.posts),
    address,
    address_resume: stringArray(raw.address_resume),
    websites: stringArray(raw.websites),
    social_networks: socialNetworks(raw.social_networks),
    phones: normalizedPhones,
    emails: stringArray(raw.emails, true),
    categories: stringArray(raw.categories),
    categoryIds: categoryIdsFrom(raw.i18n) ?? stringArray(raw.categoryIds),
    services: unknownArray(raw.services),
    times: unknownArray(raw.times),
    reviews: reviews(raw.reviews),
    price: text(raw.price),
    stars: text(raw.stars),
    ads: typeof raw.ads === 'boolean' ? raw.ads : undefined,
    claim: text(raw.claim),
    cnpj: text(raw.cnpj),
    mobile:
      typeof raw.mobile === 'boolean'
        ? raw.mobile
        : normalizedPhones?.some((phone) => phone.mobile) || undefined,
    lookalike: raw.lookalike === true || undefined,
    // Parecidos do Google e o porquê do semelhante (decisões 178 a 186). Antes eram descartados.
    similars: cids(raw.similars),
    lookalikeOf: raw.lookalike === true ? cids(raw.lookalikeOf, 20) : undefined,
    lookalikeScore:
      raw.lookalike === true ? clampScore(numberValue(raw.lookalikeScore)) : undefined,
    lookalikeLevel:
      raw.lookalike === true ? positiveInteger(numberValue(raw.lookalikeLevel)) : undefined,
    lookalikeSource:
      raw.lookalike === true &&
      (raw.lookalikeSource === 'local' || raw.lookalikeSource === 'base' || raw.lookalikeSource === 'google')
        ? raw.lookalikeSource
        : undefined,
    lookalikeRound: raw.lookalike === true ? text(raw.lookalikeRound) : undefined,
    lookalikeBack: raw.lookalike === true ? cids(raw.lookalikeBack, 20) : undefined,
    removedAt: text(raw.removedAt),
    enhancedAt: text(raw.enhancedAt),
    enhancedCount: enhancedCount(raw.enhancedCount),
    createdAt: context.createdAt,
  };
  normalized.score = numberValue(raw.score) ?? scorePlace(normalized);
  return normalized;
}

/** Normaliza e deduplica o lote por `cid`; a primeira ocorrência preserva seus metadados. */
export function normalizePlaceBatch(
  rawBatch: readonly unknown[],
  context: NormalizePlaceContext,
): LocalPlace[] {
  const resolvedContext = {
    listId: context.listId,
    createdAt: context.createdAt ?? new Date().toISOString(),
    country: context.country,
  };
  const output = new Map<string, LocalPlace>();

  for (const candidate of rawBatch) {
    if (!isRecord(candidate)) continue;
    const normalized = normalizePlace(candidate, resolvedContext);
    if (!normalized || output.has(normalized.key)) continue;

    if (context.zeroPoint && normalized.location) {
      const distance = distanceBetweenPlaces(normalized.location, context.zeroPoint);
      if (distance != null && distance >= 50_000) continue;
    }
    output.set(normalized.key, normalized);
  }
  return [...output.values()];
}
